/**
 * VideoPlayer.tsx
 *
 * Fully branded video player — zero third-party logos.
 *
 * Behaviour:
 * 1. Shows a clean thumbnail with a play button (our branding only).
 * 2. On tap: if a self-hosted MP4 exists in Supabase → plays it via <video>.
 *            if not → falls back to YouTube embed (reduced branding params).
 * 3. The self-hosted check is cached per session so there's no flickering.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2, RotateCcw } from 'lucide-react';
import {
  isHostedVideoAvailable,
  getHostedVideoUrl,
  getYouTubeEmbedUrl,
} from '../../services/videoService';
import './VideoPlayer.css';

export interface VideoPlayerProps {
  /** Supabase section ID used to build the storage path */
  sectionId: string;
  /** Unique video ID used to build the storage path */
  videoId: string;
  /** YouTube video ID — used as fallback if self-hosted not available */
  youtubeId: string;
  /** Title shown in the player overlay */
  title: string;
  /** Duration in minutes shown on thumbnail */
  durationMin: number;
  /** Active language for bilingual self-hosted videos */
  lang: 'nl' | 'en' | 'ar';
  /** Optional custom thumbnail URL — falls back to YouTube hqdefault */
  thumbnailUrl?: string;
}

type PlayerState = 'idle' | 'loading' | 'hosted' | 'youtube';

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  sectionId,
  videoId,
  youtubeId,
  title,
  durationMin,
  lang,
  thumbnailUrl,
}) => {
  const [playerState, setPlayerState] = useState<PlayerState>('idle');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const thumb = thumbnailUrl ?? `https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`;

  // Auto-hide controls after 3 s of inactivity
  const resetControlsTimer = useCallback(() => {
    setShowControls(true);
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    if (isPlaying) {
      controlsTimerRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    };
  }, []);

  // ── Play handler ──────────────────────────────────────────
  const handlePlay = useCallback(async () => {
    setPlayerState('loading');
    const hosted = await isHostedVideoAvailable(sectionId, videoId, lang);
    setPlayerState(hosted ? 'hosted' : 'youtube');
  }, [sectionId, videoId, lang]);

  // ── Native video controls ─────────────────────────────────
  const handleVideoTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleVideoLoaded = () => {
    setDuration(videoRef.current?.duration ?? 0);
    videoRef.current?.play();
    setIsPlaying(true);
    resetControlsTimer();
  };

  const togglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setIsPlaying(true);
    } else {
      v.pause();
      setIsPlaying(false);
      setShowControls(true);
    }
    resetControlsTimer();
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
    resetControlsTimer();
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const t = (parseFloat(e.target.value) / 100) * v.duration;
    v.currentTime = t;
    setProgress(parseFloat(e.target.value));
    resetControlsTimer();
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    videoRef.current?.requestFullscreen?.();
  };

  const handleRestart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
    resetControlsTimer();
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className={`vp-root ${playerState}`} onMouseMove={resetControlsTimer}>

      {/* ── Idle: show thumbnail ── */}
      {playerState === 'idle' && (
        <button className="vp-thumbnail-btn" onClick={handlePlay} aria-label={`Play: ${title}`}>
          <img className="vp-thumbnail" src={thumb} alt={title} loading="eager" />
          <div className="vp-overlay">
            <div className="vp-play-btn">
              <Play className="vp-play-icon" size={28} />
            </div>
            <span className="vp-duration-pill">{durationMin} min</span>
          </div>
        </button>
      )}

      {/* ── Loading spinner ── */}
      {playerState === 'loading' && (
        <div className="vp-loading">
          <img className="vp-thumbnail vp-thumbnail--blurred" src={thumb} alt="" />
          <div className="vp-spinner" />
        </div>
      )}

      {/* ── Self-hosted video ── */}
      {playerState === 'hosted' && (
        <div className="vp-native" onClick={resetControlsTimer}>
          <video
            ref={videoRef}
            className="vp-video"
            src={getHostedVideoUrl(sectionId, videoId, lang)}
            playsInline
            onTimeUpdate={handleVideoTimeUpdate}
            onLoadedMetadata={handleVideoLoaded}
            onEnded={() => { setIsPlaying(false); setShowControls(true); }}
          />

          {/* Custom controls — zero third-party branding */}
          <div className={`vp-controls ${showControls ? 'vp-controls--visible' : ''}`}>
            <input
              type="range"
              className="vp-progress"
              min={0}
              max={100}
              step={0.1}
              value={progress}
              onChange={handleSeek}
              aria-label="Seek"
            />
            <div className="vp-controls-row">
              <div className="vp-controls-left">
                <button onClick={togglePlayPause} className="vp-ctrl-btn" aria-label={isPlaying ? 'Pause' : 'Play'}>
                  {isPlaying ? <Pause size={18} /> : <Play size={18} />}
                </button>
                <button onClick={handleRestart} className="vp-ctrl-btn" aria-label="Restart">
                  <RotateCcw size={16} />
                </button>
                <button onClick={toggleMute} className="vp-ctrl-btn" aria-label={isMuted ? 'Unmute' : 'Mute'}>
                  {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                {duration > 0 && (
                  <span className="vp-time">
                    {formatTime((progress / 100) * duration)} / {formatTime(duration)}
                  </span>
                )}
              </div>
              <div className="vp-controls-right">
                <button onClick={handleFullscreen} className="vp-ctrl-btn" aria-label="Fullscreen">
                  <Maximize2 size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── YouTube fallback ── */}
      {playerState === 'youtube' && (
        <iframe
          className="vp-iframe"
          src={getYouTubeEmbedUrl(youtubeId)}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
    </div>
  );
};
